import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { InstitutionRepo } from '@repos/institution/institution.service';
import { InstitutionBranchRepo } from '@repos/institutionBranch/institution-branch.service';
import { FinancialInstitutionBranchSearchState } from '@state/algolia-search/financial-institution-branch-search';
import { FinancialInstitutionSearchState } from '@state/algolia-search/financial-institution-search';
import { FinancialInstitutionSearchDispatch } from '@state/algolia-search/financial-institution-search/financial-institution-search.dispatch';
import { take } from 'rxjs/operators';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { CreateOrUpdateInstitutionModel } from '../create-branch/create-branch.component';
import {
	CreateInstitutionComponent,
	CreateInstitutionData,
} from '../create-institution/create-institution.component';
import {
	AlgoliaFinancialInstitution,
	AlgoliaFinancialInstitutionBranch,
} from '@fnModels/algolia/financial_institutions';
import LogRocket from 'logrocket';
import { Action } from '@state/utils/models';
import {
	FinancialInstitutionLender,
	FinancialInstitutionTypes,
	MortgageeType,
} from '@models/lender';
import { MatterFileType } from '@models/matter';
import { SnackbarService } from '@services/ui/snackbar.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
export enum DropdownSelect {
	CREATE = 'create',
	SELECT_INSTITUTION = 'selectInstitution',
	SELECT_BRANCH = 'selectBranch',
}
export interface onSelectInstitution {
	financialInstitution: FinancialInstitutionLender;
	action: Action;
}
@UntilDestroy()
@Component({
	selector: 'app-institution-search',
	templateUrl: './search.component.html',
	standalone: false,
})
export class InsitutionSearchComponent {
	public loading = false;
	public ICON = ICON;
	public financialInstitutionName = '';
	public institutionModel: CreateOrUpdateInstitutionModel;
	public dropdownSelect = DropdownSelect;

	@Output() onSelected = new EventEmitter<onSelectInstitution>();
	@Output() onCreate = new EventEmitter<string>();

	@Input() mortgageeType: MortgageeType;
	@Input() includeFinancialInstitutions: boolean;
	@Input() includeTrustCompanies: boolean;
	@Input() fileType: MatterFileType;
	@Input() showDischargeFields: boolean;
	@Input() pushChangesToGlobalData;

	@ViewSelectSnapshot(FinancialInstitutionSearchState.getList)
	financialInstitutions;
	@ViewSelectSnapshot(FinancialInstitutionBranchSearchState.getList)
	financialInstitutionsBranches;

	constructor(
		private institutionDispatch: FinancialInstitutionSearchDispatch,
		private matDialog: MatDialog,
		private institutionRepo: InstitutionRepo,
		private branchRepo: InstitutionBranchRepo,
		private snackbar: SnackbarService,
	) {}

	search = (ev) => {
		const searchParams: { [key in FinancialInstitutionTypes]?: boolean } = {};

		if (this.mortgageeType !== MortgageeType.OTHER) {
			searchParams[FinancialInstitutionTypes.CREDIT_UNION] = true;
			searchParams[FinancialInstitutionTypes.GENERAL_FINANCIAL_INSTITUTION] =
				true;
			searchParams[FinancialInstitutionTypes.TRUST_COMPANY] = true;
		} else {
			searchParams[FinancialInstitutionTypes.OTHER] = true;
		}

		this.institutionDispatch.Search(ev, searchParams);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSelect = async (ev: DropdownResult<any>) => {
		this.loading = true;
		if (ev.groupType === DropdownSelect.SELECT_INSTITUTION) {
			try {
				// fetch institution
				const _doc = await this.institutionRepo
					.Get((ev.result as AlgoliaFinancialInstitution)?.id)
					.pipe(take(1))
					.toPromise();
				this.onSelected.emit({
					financialInstitution: {
						...(_doc.data() as FinancialInstitutionLender),
						id: _doc.id,
					},
					action: Action.SET,
				});
			} catch (err) {
				LogRocket.error('Unable to select financial institution', err);
				console.log('Unable to select financial institution', err);
			} finally {
				this.loading = false;
			}
		} else if (ev.groupType === DropdownSelect.SELECT_BRANCH) {
			try {
				const _doc = await this.institutionRepo
					.Get((ev.result as AlgoliaFinancialInstitutionBranch)?.institutionId)
					.pipe(take(1), untilDestroyed(this))
					.toPromise();
				const _branchDoc = await this.branchRepo
					.Get((ev.result as AlgoliaFinancialInstitutionBranch)?.id, {
						financialInstitutionId: (
							ev.result as AlgoliaFinancialInstitutionBranch
						)?.institutionId,
					})
					.pipe(take(1), untilDestroyed(this))
					.toPromise();

				const insitution = {
					..._doc.data(),
					id: _doc.id,
					branch: { ..._branchDoc.data(), id: _branchDoc.id },
				};
				this.onSelected.emit({
					financialInstitution: insitution as FinancialInstitutionLender,
					action: Action.SET,
				});
			} catch (err) {
				LogRocket.error('Unable to select financial institution branch', err);
				console.log('Unable to select financial institution branch', err);
			} finally {
				this.loading = false;
			}
		} else if (ev.groupType === DropdownSelect.CREATE) {
			// load create component
			const data: CreateInstitutionData = {
				showDischargeFields: this.showDischargeFields,
				fileType: this.fileType,
				create: true,
				pushChangesToGlobalData: this.pushChangesToGlobalData,
				institution: {
					name: this.financialInstitutionName,
				} as FinancialInstitutionLender,
				type: this.mortgageeType,
			};
			this.matDialog
				.open(CreateInstitutionComponent, {
					data,
					width: '80vw',
					maxWidth: '800px',
				})
				.afterClosed()
				.pipe(take(1), untilDestroyed(this))
				.subscribe((result) => {
					if (result) {
						this.onSelected.emit({
							financialInstitution: result as FinancialInstitutionLender,
							action: Action.CREATE,
						});

						if (this.pushChangesToGlobalData) {
							this.snackbar.openSnackBar({
								text: 'Lender details submitted for review.',
								type: 'success',
							});
						}
					}
					this.loading = false;
				});
		}
	};
}
